import React, { useEffect, useState, useRef } from "react";
import styles from "./AddQuestions.module.scss";
import RichTextEditor from "@/components/HtmlEditor/RichTextEditor";
import {
  createQuestionAPI,
  fetchQuestionByIdAPI,
  updateQuestionAPI,
} from "@/services/api/Question";
import { useDispatch, useSelector } from "react-redux";
import { fetchQuestionsByTags } from "@/store/slices/QuestionSlice";
import type { AppDispatch } from "@/store/store";
import type { RootState } from "@/store/rootReducer";
import DeleteIcon from "@/components/SVGComponents/DeleteIcon";
import ReactSelect from "react-select";

interface Props {
  onClose: () => void;
  editId?: string | null;
  refreshList: () => void;
}

type QuestionType = "mcq" | "short";

interface MCQOption {
  id: number;
  text: string;
}

interface Tag {
  id: number;
  name: string;
}

interface Question {
  id: number;
  type: QuestionType;
  question: string;
  options?: MCQOption[];
  correctAnswers?: number[];
  shortAnswers?: string[];
}

const AddQuestions: React.FC<Props> = ({ onClose, editId, refreshList }) => {
  const dispatch = useDispatch<AppDispatch>();
  const modalBodyRef = useRef<HTMLDivElement>(null);
  const shouldScrollRef = useRef(false);

  const { questionTags } = useSelector(
    (state: RootState) => state.questions
  );

  const [name, setName] = useState("");
  const [difficulty, setDifficulty] = useState("EASY");
  const [tags, setTags] = useState<Tag[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const removeTag = (id: number) => {
    setTags(tags.filter((t) => t.id !== id));
  };

  const removeQuestion = (id: number) => {
    setDeleteId(id);
  };

  const confirmRemoveQuestion = () => {
    if (deleteId !== null) {
      setQuestions((prev) => prev.filter((q) => q.id !== deleteId));
      setDeleteId(null);
    }
  };

  const addMCQ = () => {
    const newQ: Question = {
      id: Date.now(),
      type: "mcq",
      question: "",
      options: [
        { id: 1, text: "" },
        { id: 2, text: "" },
        { id: 3, text: "" },
        { id: 4, text: "" },
      ],
      correctAnswers: [],
    };
    setQuestions([...questions, newQ]);
    shouldScrollRef.current = true;
  };

  const addShort = () => {
    const newQ: Question = {
      id: Date.now(),
      type: "short",
      question: "",
      shortAnswers: [""],
    };
    setQuestions([...questions, newQ]);
    shouldScrollRef.current = true;
  };

  const updateQuestion = (id: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q) => (q.id === id ? { ...q, question: value } : q))
    );
  };

  const updateOption = (qId: number, optId: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? {
            ...q,
            options: q.options?.map((opt) =>
              opt.id === optId ? { ...opt, text: value } : opt
            ),
          }
          : q
      )
    );
  };

  const removeOption = (qId: number, optId: number) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === qId && q.options && q.options.length > 2) {
          return {
            ...q,
            options: q.options.filter((opt) => opt.id !== optId),
          };
        }
        return q;
      })
    );
  };

  const addOption = (qId: number) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id === qId && q.options) {
          const nextId =
            Math.max(...(q.options.map((opt) => opt.id) || [0])) + 1;
          return {
            ...q,
            options: [...q.options, { id: nextId, text: "" }],
          };
        }
        return q;
      })
    );
    shouldScrollRef.current = true;
  };

  const toggleCorrect = (qId: number, optId: number) => {
    setQuestions((prev) =>
      prev.map((q) => {
        if (q.id !== qId) return q;
        const exists = q.correctAnswers?.includes(optId);
        return {
          ...q,
          correctAnswers: exists
            ? q.correctAnswers?.filter((id) => id !== optId)
            : [...(q.correctAnswers || []), optId],
        };
      })
    );
  };

  const updateShortAnswer = (qId: number, index: number, value: string) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? {
            ...q,
            shortAnswers: q.shortAnswers?.map((ans, i) =>
              i === index ? value : ans
            ),
          }
          : q
      )
    );
  };

  const addShortAnswer = (qId: number) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId
          ? { ...q, shortAnswers: [...(q.shortAnswers || []), ""] }
          : q
      )
    );
    shouldScrollRef.current = true;
  };

  const removeShortAnswer = (qId: number, index: number) => {
    setQuestions((prev) =>
      prev.map((q) =>
        q.id === qId && q.shortAnswers && q.shortAnswers.length > 1
          ? {
            ...q,
            shortAnswers: q.shortAnswers.filter((_, i) => i !== index),
          }
          : q
      )
    );
  };

  const handleSave = async () => {
    try {
      const payload = {
        name,
        difficultyLevel: difficulty,
        tagIds: tags.map((tag) => tag.id),
        questions: questions.map((q) => {
          if (q.type === "mcq") {
            return {
              title: q.question,
              questionType: "MULTIPLE_CHOICE",
              description: q.question,
              options: q.options?.map((opt) => ({
                optionValue: opt.text,
                isCorrect: q.correctAnswers?.includes(opt.id),
              })),
            };
          }
          return {
            title: q.question,
            questionType: "SHORT_ANSWER",
            description: q.question,
            options: q.shortAnswers
              ?.filter((ans) => ans.trim() !== "")
              .map((ans) => ({
                optionValue: ans,
                isCorrect: true,
              })) || [],
          };
        }),
      };

      if (editId) {
        await updateQuestionAPI({ ...payload, id: editId }, editId);
      } else {
        await createQuestionAPI(payload);
      }

      refreshList();
      onClose();
    } catch (error) {
      console.error(error);
    }
  };

  const [tagSearch, setTagSearch] = useState("");

  useEffect(() => {
    const handler = setTimeout(() => {
      dispatch(fetchQuestionsByTags({ pageNo: 1, perPage: 50, search: tagSearch, reset: true }));
    }, 500);
    return () => clearTimeout(handler);
  }, [tagSearch]);

  useEffect(() => {
    if (!editId) return;

    const loadData = async () => {
      try {
        const res = await fetchQuestionByIdAPI(editId);

        setName(res?.data?.name || "");
        setDifficulty(res?.data?.difficultyLevel || "EASY");
        setTags(res?.data?.tags || []);

        setQuestions(
          res?.data?.questions?.map((q: any, index: number) => {
            const isMCQ = q.type === "MULTIPLE_CHOICE" || q.questionType === "MULTIPLE_CHOICE";
            return {
              id: Date.now() + index,
              type: isMCQ ? "mcq" : "short",
              question: q.title,
              options: isMCQ
                ? q.options?.map((opt: any, i: number) => ({
                  id: i + 1,
                  text: opt.optionValue || opt.optionvalue,
                }))
                : undefined,
              correctAnswers: isMCQ
                ? q.options
                  ?.map((o: any, i: number) =>
                    o.isCorrect || o.iscorrect ? i + 1 : -1
                  )
                  .filter((val: number) => val !== -1)
                : undefined,
              shortAnswers: !isMCQ
                ? (() => {
                  const loadedAnswers = q.options
                    ?.filter((o: any) => o.isCorrect || o.iscorrect)
                    .map((o: any) => o.optionValue || o.optionvalue) || [];
                  return loadedAnswers.length > 0 ? loadedAnswers : [""];
                })()
                : [],
            };
          })
        );
      } catch (err) {
        console.error(err);
      }
    };

    loadData();
  }, [editId]);

  useEffect(() => {
    if (shouldScrollRef.current && modalBodyRef.current) {
      shouldScrollRef.current = false;
      const container = modalBodyRef.current;
      setTimeout(() => {
        container.scrollTo({
          top: container.scrollHeight,
          behavior: "smooth",
        });
      }, 50);
    }
  }, [questions]);

  return (
    <div
      className={styles.modalOverlay}
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className={styles.modal}>
        {/* HEADER */}
        <div className={styles.modalHeader}>
          <h3>{editId ? "Edit question set" : "New question set"}</h3>
          <button onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {/* BODY */}
        <div ref={modalBodyRef} className={styles.modalBody}>

          {/* NAME */}
          <div className={styles.inputGroup}>
            <label>Question Set Title</label>
            <input
              type="text"
              placeholder="e.g. JavaScript Fundamentals"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* DIFFICULTY */}
          <div className={styles.fullRow}>
            <label>Difficulty</label>
            <select
              value={difficulty}
              onChange={(e) => setDifficulty(e.target.value)}
            >
              <option value="EASY">EASY</option>
              <option value="MEDIUM">MEDIUM</option>
              <option value="HARD">HARD</option>
            </select>
          </div>

          {/* TAGS */}
          <div className={styles.fullRow}>
            <label>Tags (max 5)</label>

            <ReactSelect
              inputValue={tagSearch}
              onInputChange={(val, { action }) => {
                if (action === 'input-change') setTagSearch(val);
              }}
              onChange={(selected: any) => {
                if (selected) {
                  const selectedTag = questionTags.find(
                    (tag: Tag) => tag.id === selected.value
                  );
                  if (selectedTag && tags.length < 5) {
                    setTags([...tags, selectedTag]);
                  }
                  setTagSearch("");
                }
              }}
              options={questionTags
                .filter((tag: Tag) => !tags.some((t) => t.id === tag.id))
                .map((tag: Tag) => ({ label: tag.name, value: tag.id }))}
              placeholder="Search and select tags..."
              value={null}
              menuPortalTarget={document.body}
              styles={{
                control: (base) => ({
                  ...base,
                  backgroundColor: 'var(--surface-2, #f8fafc)',
                  borderColor: 'var(--border, #cbd5e1)',
                  borderRadius: '8px',
                  boxShadow: 'none',
                  color: 'var(--text, #1e293b)',
                  '&:hover': { borderColor: 'var(--accent, #3b82f6)' }
                }),
                input: (base) => ({
                  ...base,
                  color: 'var(--text, #1e293b)',
                  margin: 0,
                  paddingTop: 0,
                  paddingBottom: 0,
                }),
                singleValue: (base) => ({
                  ...base,
                  color: 'var(--text, #1e293b)',
                }),
                placeholder: (base) => ({
                  ...base,
                  color: 'var(--text-3, #94a3b8)',
                }),
                menuPortal: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
                menu: (base) => ({
                  ...base,
                  backgroundColor: 'var(--surface, #ffffff)',
                  border: '1px solid var(--border, #e2e8f0)',
                  zIndex: 9999,
                }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isSelected
                    ? 'var(--accent, #3b82f6)'
                    : state.isFocused
                      ? 'var(--surface-2, #f1f5f9)'
                      : 'var(--surface, #ffffff)',
                  color: state.isSelected
                    ? '#ffffff'
                    : 'var(--text, #1e293b)',
                  cursor: 'pointer',
                  '&:active': {
                    backgroundColor: 'var(--accent, #3b82f6)'
                  }
                })
              }}
            />

            {/* Selected Tags */}
            <div className={styles.tags}>
              {tags.map((tag) => (
                <span key={tag.id} className={styles.tag}>
                  {tag.name}
                  <span onClick={() => removeTag(tag.id)}>✖</span> {/* 👈 removing adds it back to dropdown automatically */}
                </span>
              ))}
            </div>
          </div>

          {/* QUESTIONS */}
          {questions.map((q, index) => (
            <div key={q.id} className={styles.questionBox}>
              <div className={styles.questionHeader}>
                <h4 className={styles.label}>
                  Question {index + 1}
                </h4>
                <button onClick={() => removeQuestion(q.id)}><DeleteIcon /></button>
              </div>

              <RichTextEditor
                value={q.question}
                onChange={(val) => updateQuestion(q.id, val)}
              />

              {q.type === "mcq" && (
                <>
                  {q.options?.map((opt) => (
                    <div key={opt.id} className={styles.optionRow}>
                      <input
                        type="checkbox"
                        checked={q.correctAnswers?.includes(opt.id)}
                        onChange={() => toggleCorrect(q.id, opt.id)}
                      />
                      <input
                        className={styles.optionMCQ}
                        placeholder={`Option`}
                        value={opt.text}
                        onChange={(e) =>
                          updateOption(q.id, opt.id, e.target.value)
                        }
                      />
                      {q.options && q.options.length > 2 && (
                        <button onClick={() => removeOption(q.id, opt.id)}>
                          ✖
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    className={styles.addOptionBtn}
                    onClick={() => addOption(q.id)}
                  >
                    + Add Option
                  </button>
                </>
              )}

              {q.type === "short" && (
                <div style={{ marginTop: '12px' }}>
                  <label style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-2, #475569)', marginBottom: '8px', display: 'block' }}>
                    Correct Answer(s) (to match candidate's response)
                  </label>
                  {q.shortAnswers?.map((ans, ansIdx) => (
                    <div key={ansIdx} className={styles.optionRow}>
                      <input
                        className={styles.optionMCQ}
                        placeholder={`Enter correct answer...`}
                        value={ans}
                        onChange={(e) =>
                          updateShortAnswer(q.id, ansIdx, e.target.value)
                        }
                      />
                      {q.shortAnswers && q.shortAnswers.length > 1 && (
                        <button onClick={() => removeShortAnswer(q.id, ansIdx)}>
                          ✖
                        </button>
                      )}
                    </div>
                  ))}
                  <button
                    className={styles.addOptionBtn}
                    onClick={() => addShortAnswer(q.id)}
                  >
                    + Add Alternative Answer
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* FOOTER */}
        <div className={styles.modalFooter}>
          <div>
            <button onClick={addMCQ}>Add multiple choice</button>
            <button onClick={addShort}>Add short answer</button>
          </div>
          <button className={styles.saveBtn} onClick={handleSave}>
            Save question set
          </button>
        </div>
      </div>

      {deleteId !== null && (
        <div className={styles.modalOverlay} style={{ zIndex: 1100 }}>
          <div className={styles.modal} style={{ width: '400px', minHeight: 'unset', padding: '24px', textAlign: 'center' }}>
            <h3 style={{ marginTop: 0, color: 'var(--text, #1e293b)' }}>Confirm Delete</h3>
            <p style={{ color: 'var(--text-2, #475569)', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' }}>
              This is part of another assessment, so deleting it will also remove it from that assessment.
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              <button
                onClick={() => setDeleteId(null)}
                style={{ padding: '8px 16px', background: 'transparent', border: '1px solid var(--border, #e2e8f0)', borderRadius: '8px', cursor: 'pointer', color: 'var(--text-2, #64748b)' }}
              >
                Cancel
              </button>
              <button
                onClick={confirmRemoveQuestion}
                style={{ padding: '8px 16px', background: '#ef4444', border: 'none', borderRadius: '8px', cursor: 'pointer', color: '#fff', fontWeight: 'bold' }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddQuestions;